import { StateManager } from 'AppReact';
import setLoading from 'components/loading';
import Schema, { required } from 'AppReact/schema';
import getIsMobile from "components/atoms/utils/getIsMobile";
import { emailRegExp } from 'utils';
import { serverToClientMapper } from './utils/serverToClientMapper';
import { getLanguageCode, localization } from './utils';
import {
    occupantsSchema, defaultOccupant, moveInSchema, defaultMoveInData,
    petsSechema, vehicleSechema, currentResidencySchema,
    defaultcurrentResidency, employmentInformationSchema, defaultEmploymentInformation,
    emergencyContactsSchema, defaultEmergencyContacts, cardDetailsSchema, defaultCardDetails,
    defaultUpdateApplication, defaultDocsSchema, defaultDocs, availableUnit, 
    optionaldocsSechema, emailExistSchema, defaultEmailExist,
} from './types';
import validateAll from './validations/validate';

const isMobile = getIsMobile();

const stateManager = StateManager([
    {
        name: 'selectedPropertyId', isRemote: false, defaultValue: null,
        schema: Schema('integer', 'primitive')
    },
    {
        name: 'property', isRemote: true, defaultValue: {},
        index: ({ propertyCode }) => `/api/properties?code=${propertyCode}`
    },
    {
        name: 'propertyCode', isRemote: false, defaultValue: '',
        schema: Schema('string', 'primitive'),
    },
    {
        name: 'selectedTabId', isRemote: false, defaultValue: 'landingPage',
        schema: Schema('string', 'primitive'),
    },
    {
        name: 'occupants', isRemote: false, defaultValue: [defaultOccupant],
        schema: Schema(occupantsSchema, 'array')
    },
    {
        name: 'moveInInformation', isRemote: false, defaultValue: defaultMoveInData,
        schema: Schema(moveInSchema, 'object')
    },
    {
        name: 'pets', isRemote: false, defaultValue: [],
        schema: Schema(petsSechema, 'array')
    },
    {
        name: 'havePets', isRemote: false, defaultValue: false,
        schema: Schema('boolean', 'primitive')
    },
    {
        name: 'vehicles', isRemote: false, defaultValue: [],
        schema: Schema(vehicleSechema, 'array')
    },
    {
        name: 'haveVehicle', isRemote: false, defaultValue: false,
        schema: Schema('boolean', 'primitive')
    },
    {
        name: 'currentResidency', isRemote: false, defaultValue: defaultcurrentResidency,
        schema: Schema(currentResidencySchema, 'object')
    },
    {
        name: 'employmentInformations', isRemote: false, defaultValue: [defaultEmploymentInformation],
        schema: Schema(employmentInformationSchema, 'array')
    },
    {
        name: 'emergencyContacts', isRemote: false, defaultValue: [defaultEmergencyContacts],
        schema: Schema(emergencyContactsSchema, 'array')
    },
    {
        name: 'uploadedDoc', isRemote: false, defaultValue: defaultDocs,
        schema: Schema(defaultDocsSchema, 'object')
    },
    {
        name: 'optionalDocs', isRemote: false, defaultValue: [],
        schema: Schema(optionaldocsSechema, 'array')
    },
    {
        name: 'photoIDs', isRemote: false, defaultValue: [],
        schema: Schema(optionaldocsSechema, 'array')
    },
    {
        name: 'leaseholderName', isRemote: false, defaultValue: '',
        schema: Schema({ leaseholderName: required('string') })
    },
    {
        name: 'termsModal', isRemote: false, defaultValue: false,
        schema: Schema({ termsModal: required('boolean') })
    },
    {
        name: 'termsAccepted', isRemote: false, defaultValue: false,
        schema: Schema({ termsAccepted: required('boolean') })
    },
    {
        name: 'paymentAuthorization', isRemote: false, defaultValue: false,
        schema: Schema({ paymentAuthorization: required('boolean') })
    },
    {
        name: 'cardDetails', isRemote: false, defaultValue: defaultCardDetails,
        schema: Schema(cardDetailsSchema, 'object')
    },
    {
        name: 'paidFee', isRemote: false, defaultValue: { paidFee: '' },
        schema: Schema({ paidFee: required('string') })
    },
    {
        name: 'savedForm', isRemote: true, defaultValue: null,
        create: () => '/api/forms',
    },
    {
        name: 'allowSubmit', isRemote: false, defaultValue: false,
        schema: Schema({ allowSubmit: required('boolean') })
    },
    {
        name: 'submitApplication', isRemote: true, defaultValue: null,
        create: () => '/api/rent_applications'
    },
    {
        name: 'retrieveApplication', isRemote: true, defaultValue: {},
        create: () => `/api/forms/get`
    },
    {
        name: 'savedFormID', isRemote: false, defaultValue: 0,
        schema: Schema({ savedFormID: required('number') })
    },
    {
        name: 'isEditMode', isRemote: false, defaultValue: false,
        schema: Schema({ isEditMode: required('boolean') })
    },
    {
        name: 'isSavedApplication', isRemote: false, defaultValue: false,
        schema: Schema({ isSavedApplication: required('boolean') })
    },
    {
        name: 'updateApplication', isRemote: true, defaultValue: defaultUpdateApplication,
        update: ({ id }) => {
            return `/api/applications/${id}`
        }
    },
    {
        name: 'language', isRemote: false, defaultValue: { },
    },
    {
        name: 'selectedLanguage', isRemote: false, defaultValue: 'en_us',
        schema: Schema({ selectedLanguage: required('string') })
    },
    {
        name: 'availableUnits', isRemote: true, defaultValue: [],
        index: ({ property }) => `/api/rent_applications?available_units=true&property_id=${property.id}&client_schema=${window.CLIENT_SCHEMA}`,
        schema: Schema(availableUnit, 'object')
    },
    {
        name: 'toggleWebCam', isRemote: false, defaultValue: '',
        schema: Schema({ toggleWebCam: required('string') })
    },
    {
        name: 'lastActiveScreen', isRemote: false, defaultValue: 'landingPage',
        schema: Schema({ lastActiveScreen: required('string') })
    },
    {
        name: 'previousMoveInDate', isRemote: false, defaultValue: '',
        schema: Schema({ previousMoveInDate: required('string') })
    },
    {
        name: 'isEmailExist', isRemote: true, defaultValue: defaultEmailExist,
        create: () => '/api/forms/get', schema: Schema(emailExistSchema)
    },
    {
        name: 'sendEmail', isRemote: true, defaultValue: {},
        create: () => '/api/forms/send_pin'
    },
])

export const initialize = async (manager = stateManager) => {
    setLoading(true);

    // Retreive data of submited application
    if (window.PROPERTY_DATA) {
        manager.setProperty(window.PROPERTY_DATA)
        manager.setAvailableUnits(window.AVAILABLE_UNITS)

        const { id, occupants, move_in, pets, vehicles, employments, histories, emergency_contacts } = window.APPLICATION_JSON
        const form = { occupants, move_in, pets, vehicles, employments, histories, emergency_contacts }

        //Setting language English as window.APPLICATION_JSON does't return language
        manager.setLanguage(localization('en_us'))
        manager.setSavedFormID(id)
        const applicationMode = window.location.pathname.split("/").reverse()[0]
        manager.setIsEditMode(applicationMode === 'edit')

        // "2022-10-20" - YYYY-MM-DD
        stateManager.setPreviousMoveInDate(move_in?.expected_move_in)
        

        try {
            serverToClientMapper(form, manager)
            manager.setSelectedTabId("occupants")
            setLoading(false)
        } catch (e) {
            setLoading(false)
        }

        setLoading(false)
        return
    };

    const params = window.location.pathname.split('/');
    const propertyCode = params[1];
    const email = params[2];
    const pin = params[3];

    // Initilize application
    manager.setPropertyCode(propertyCode)
    const response = await manager.fetchProperty()
    manager.setProperty(response.data)    
    if (window.CLIENT_SCHEMA === 'dasmen') manager.fetchAvailableUnits()
                                           .catch(() => manager.setAvailableUnits([]))

    // stop execution to fetch saved application
    if (!emailRegExp(email)) {
        const fullPath = window.location.href
        if(fullPath[fullPath.length - 1] == '/') window.location.href = fullPath.slice(0, fullPath.length - 1)
        setLoading(false)
        return
    }

    // fetch saved application
    const payload = { client_schema: window.CLIENT_SCHEMA, property_id: response.data.id, email, pin };
    const r = await manager.saveRetrieveApplication(payload);

    const form = r.data.form
    manager.setSavedFormID(r.data.id)
    manager.setIsSavedApplication(true)

    const { lang } = form;
    const langCode = getLanguageCode(lang)
    manager.setSelectedLanguage(langCode || 'en_us')
    manager.setLanguage(localization(langCode))
    

    try {
        serverToClientMapper(form, manager)
        const lastActiveScreen = form?.last_active_screen
        manager.setSelectedTabId(lastActiveScreen || 'occupants')

        validateAll(true)
        setLoading(false)

    } catch (e) {
        setLoading(false)
    }
}

export default stateManager;
